'use client';

import classnames from 'classnames';
import React, { CSSProperties, SyntheticEvent } from 'react';

import { ButtonStyle } from './ButtonStyle.enum';

export enum ContentAlignment {
    start,
    center,
    end
}

export type ButtonProps = {
    children?: React.ReactNode;
    id?: string;
    className?: string;
    onClick?: (event: SyntheticEvent) => void;
    style?: ButtonStyle | CSSProperties;
    contentAlignment?: ContentAlignment;
    type?: 'button' | 'submit' | 'reset';
    leadingIcon?: false | string | React.JSX.Element;
    // `false` is accepted (as on leadingIcon) so `trailingIcon={isBusy && <Spinner />}` works.
    trailingIcon?: false | string | React.JSX.Element;
    disabled?: boolean;
    allowLineBreak?: boolean;
    labelClassName?: string;
    labelUppercase?: boolean;
    /** Accessible name — required for icon-only buttons (no children). */
    'aria-label'?: string;
    /** Set on buttons that open an overlay, together with `aria-expanded`. */
    'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
    /** Whether the overlay this button controls is currently open. */
    'aria-expanded'?: boolean;
    /** Set on a button that toggles a mode on and off, e.g. a list's sorting mode. */
    'aria-pressed'?: boolean;
};

function Button(props: ButtonProps, ref: React.LegacyRef<HTMLButtonElement> | undefined): React.JSX.Element {
    const {
        children,
        id,
        className,
        onClick,
        style,
        contentAlignment,
        type,
        leadingIcon,
        trailingIcon,
        allowLineBreak,
        labelUppercase = false
    } = props;

    const disabled = props.disabled ?? false;

    let buttonStyle: string, hoverEffect: string;

    switch (style) {
        case ButtonStyle.frameless:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `text-[var(--primary-color)] ${disabled ? '' : hoverEffect}`;
            break;
        case ButtonStyle.frameless_on_white_bg:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `text-[var(--primary-dark-color)] ${disabled ? '' : hoverEffect}`;
            break;
        case ButtonStyle.inverted:
            hoverEffect = 'hover:bg-[var(--primary-color)] hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-2 border-transparent text-[var(--primary-color)] ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.inverted_on_white_bg:
            hoverEffect = 'hover:bg-[var(--primary-middle-color)] hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-2 border-transparent text-[var(--on-primary-color)] ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.outlined:
            hoverEffect = 'hover:bg-[var(--primary-color)] hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-solid border-2 border-[var(--primary-color)] text-[var(--primary-color)] ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.outlined_on_white_bg:
            hoverEffect =
                'hover:bg-[var(--primary-middle-color)] hover:text-[var(--on-primary-color)] hover:border-transparent';
            buttonStyle = `border-solid border-2 border-[var(--primary-dark-color)] text-[var(--primary-dark-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.outlined_gray_on_white_bg:
            hoverEffect = 'hover:bg-gray-light hover:opacity-80 hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-solid border-2 border-gray-dark text-gray-dark ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.outlined_no_padding:
            hoverEffect = 'hover:bg-[var(--primary-color)] hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-solid border-2 border-[var(--primary-color)] text-[var(--primary-color)] ${disabled ? '' : hoverEffect} py-2 px-8`;
            break;
        case ButtonStyle.gray_and_white:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-white/20 text-white ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.gray_and_white_no_padding:
            hoverEffect = 'hover:text-gray-dark hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-white/20 text-white ${disabled ? '' : hoverEffect} py-2 px-8`;
            break;
        case ButtonStyle.text_only:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.text_only_no_padding:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `${disabled ? '' : hoverEffect}`;
            break;
        case ButtonStyle.default_no_padding:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[var(--primary-color)] text-[var(--on-primary-color)] ${disabled ? '' : hoverEffect} py-2 px-8`;
            break;
        case ButtonStyle.event_request_status_button:
            hoverEffect = 'hover:text-white hover:border-white hover:opacity-80';
            buttonStyle = `border-2 border-gray bg-transparent text-gray text-body-bold-12 ${
                disabled ? '' : hoverEffect
            } py-2 px-8`;
            break;
        case ButtonStyle.default_on_white_bg:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[var(--primary-middle-color)] text-[var(--on-primary-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.default_no_padding_on_white_bg:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[var(--primary-middle-color)] text-[var(--on-primary-color)] ${disabled ? '' : hoverEffect}`;
            break;
        case ButtonStyle.ghost:
            hoverEffect = 'hover:bg-[rgba(var(--on-primary-color-rgb),0.1)] hover:text-[var(--on-primary-color)]';
            buttonStyle = `border-solid border-2 border-[var(--on-primary-color)] text-[var(--on-primary-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.delete_action:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-red text-white ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.destructive:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-transparent text-red ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        case ButtonStyle.cyan_on_dark_bg:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[rgba(var(--primary-color-rgb),0.2)] text-[var(--primary-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.light_gray:
            hoverEffect = 'hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-gray-light text-[var(--on-primary-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        default:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[var(--primary-color)] text-[var(--on-primary-color)] ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
    }

    let btnContentAlignment: string | undefined;
    switch (contentAlignment) {
        case ContentAlignment.start:
            break;
        case ContentAlignment.end:
            btnContentAlignment = 'justify-end';
            break;
        default:
            btnContentAlignment = 'justify-center';
            break;
    }

    return (
        <button
            id={id}
            className={classnames(
                className,
                buttonStyle,
                disabled ? 'opacity-50 cursor-not-allowed ' : 'active:opacity-50',
                'flex flex-row space-x-8',
                allowLineBreak === true ? 'w-min xl:w-auto' : 'flex-none',
                'outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary-color)] items-center rounded-8 font-montserrat-bold transition-[opacity,background-color] duration-150',
                btnContentAlignment,
                labelUppercase && 'uppercase'
            )}
            style={typeof style === 'string' ? undefined : style}
            aria-label={props['aria-label']}
            aria-haspopup={props['aria-haspopup']}
            aria-expanded={props['aria-expanded']}
            aria-pressed={props['aria-pressed']}
            disabled={disabled}
            onClick={(event) => {
                onClick?.(event);
            }}
            type={type}
            ref={ref}>
            {leadingIcon && <div>{leadingIcon}</div>}
            {children && (
                <div
                    className={classnames('flex my-auto', props.labelClassName, {
                        'text-button-14 md:text-button-16': !props.labelClassName
                    })}>
                    {children}
                </div>
            )}
            {trailingIcon && <div>{trailingIcon}</div>}
        </button>
    );
}

export default React.forwardRef(Button);
